import { useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import {
  InvalidTemplateContents,
  EmailContentArgsInput,
  UpsertTemplateResult,
} from 'modules/graphql/types';
import { isEmpty } from '@iterable/utils';
import { addNegativeToast } from 'modules/toastContainer';
import noOp from 'utils/noop';
import {
  GqlUpsertTemplateFailureTypes,
  GqlUpsertTemplateInvalidContentFailureTypes,
  GqlInvalidContentReason,
} from '../../common/types';
import { getErrorMessage } from '../../common/utils';
import { Config } from './types';

export type UpdateEmailTemplateRequest = {
  cacheDataFeed: boolean;
  campaignId?: number;
  content: EmailContentArgsInput;
  dateFeedIds: number[];
  // destinationFolderId?: number;
  mergeDataFeedContext: boolean;
  messageTypeId: number;
  name: string;
  referenceDataFeedsByAlias: boolean;
  templateId: number;
};

type UpdateEmailTemplateResponse = {
  updateEmailTemplate: UpsertTemplateResult;
};

export const UpdateEmailTemplateMutation = gql`
  mutation UpdateEmailTemplate(
    $cacheDataFeed: Boolean!
    $campaignId: Long
    $content: EmailContentArgsInput!
    $dataFeedIds: [Long!]!
    $mergeDataFeedContext: Boolean!
    $messageTypeId: Long!
    $name: String!
    $referenceDataFeedsByAlias: Boolean!
    $shortenLinks: Boolean!
    $templateId: Long!
  ) {
    updateEmailTemplate(
      cacheDataFeed: $cacheDataFeed
      campaignId: $campaignId
      content: $content
      dataFeedIds: $dataFeedIds
      mergeDataFeedContext: $mergeDataFeedContext
      messageTypeId: $messageTypeId
      name: $name
      referenceDataFeedsByAlias: $referenceDataFeedsByAlias
      shortenLinks: $shortenLinks
      templateId: $templateId
    ) {
      failure {
        ... on InvalidTemplateContents {
          errors {
            ... on InvalidTemplateContent {
              reasons {
                ... on JSInAnchorTag {
                  fieldName
                }
                ... on IllegalScript {
                  fieldName
                }
                ... on JSInIframeTag {
                  fieldName
                }
                ... on InvalidAttribute {
                  tagName
                  fieldName
                }
              }
              content {
                ... on EmailContentArgs {
                  locale {
                    name
                  }
                }
              }
            }
          }
        }
        ... on TemplateNotFound {
          templateId
        }
        __typename
      }
      templateId
    }
  }
`;

export const useUpdateEmailTemplate = ({
  onComplete = noOp,
  onError = noOp,
  onSuccess = noOp,
}: Config) => {
  const dispatch = useDispatch();
  const [upsertEmailTemplate, data] = useMutation<
    UpdateEmailTemplateResponse,
    UpdateEmailTemplateRequest
  >(UpdateEmailTemplateMutation);

  useEffect(() => {
    if (!data.called) return;
    if (!data.loading && data.error) {
      dispatch(addNegativeToast('An unknown error occurred'));
    }

    if (!data.loading && data?.data?.updateEmailTemplate) {
      const { failure, templateId } = data.data.updateEmailTemplate;
      const failures = (Array.isArray(failure) || failure === null ? failure : [failure]) ?? [];
      const errorMessages: string[] = [];
      const fieldErrors: Record<string, string> = {};
      failures.forEach((error) => {
        if (error.__typename === GqlUpsertTemplateFailureTypes.INVALID_TEMPLATE_CONTENTS) {
          (error as InvalidTemplateContents).errors.forEach((invalidContentError) =>
            invalidContentError.reasons.forEach((reason) => {
              const invalidContentReason = reason as GqlInvalidContentReason;
              if (
                invalidContentReason.__typename ===
                  GqlUpsertTemplateInvalidContentFailureTypes.ILLEGAL_SCRIPT ||
                invalidContentReason.__typename ===
                  GqlUpsertTemplateInvalidContentFailureTypes.JS_IN_ANCHOR_TAG ||
                invalidContentReason.__typename ===
                  GqlUpsertTemplateInvalidContentFailureTypes.JS_IN_IFRAME_TAG ||
                invalidContentReason.__typename ===
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ATTRIBUTE
              )
                errorMessages.push(getErrorMessage(invalidContentReason));
            }),
          );
        } else {
          errorMessages.push(getErrorMessage(error));
        }
      });

      errorMessages.forEach((errorMessage) => dispatch(addNegativeToast(errorMessage)));

      if (templateId) {
        onSuccess(templateId);
      } else if (!isEmpty(fieldErrors)) {
        onError(fieldErrors);
      }
    }
    onComplete();
  }, [data.loading]);
  return [upsertEmailTemplate, data] as const;
};
